/**
 * Shared scroll-direction decision for "hide on scroll-down, reveal on scroll-up".
 *
 * Both the collapsing top header (HeroHeader) and the floating bottom nav (TabBar)
 * push the same scroll container's scrollTop through this, so top and bottom chrome
 * move in lockstep. Kept pure (no DOM, no React) so the thresholds are unit-testable.
 */

/** Within this many px of the top the chrome is always shown, so it is never hidden at rest. */
export const REVEAL_ZONE_PX = 24;

/** Per-frame scroll delta below which movement is treated as jitter and ignored. */
export const DIRECTION_DELTA_PX = 6;

export interface ScrollHideState {
  hidden: boolean;
  last: number;
}

/**
 * Next hide-state for the current scrollTop `y` given the previous state.
 *
 * Near the top always shows. A clear downward move hides; a clear upward move
 * shows. Sub-threshold jitter keeps the current state. `last` always advances to
 * `y`, so the delta is measured per frame rather than accumulating slow drift.
 */
export function nextScrollHideState(y: number, prev: ScrollHideState): ScrollHideState {
  if (y <= REVEAL_ZONE_PX) return { hidden: false, last: y };
  if (y - prev.last > DIRECTION_DELTA_PX) return { hidden: true, last: y };
  if (y - prev.last < -DIRECTION_DELTA_PX) return { hidden: false, last: y };
  return { hidden: prev.hidden, last: y };
}
